import { ElementRef, Injectable } from '@angular/core';
import { WebSocketSubject } from "rxjs/internal-compatibility";
import { Message } from "./Message";
import { webSocket } from "rxjs/webSocket";
import { DataManagementService } from "../data-management/data-management.service";
import { Subject, Subscription } from "rxjs";
import { wsocket } from "../../global.config";
import { tap } from "rxjs/operators/tap";
import { ToastrService } from "ngx-toastr";
import { Vehicule } from "../../model/data-managemnt";
import { Router } from "@angular/router";
import { Path } from "../../model/historical";
import {ToastController} from "@ionic/angular";

// WebRTC configuration
const offerOptions = {
  offerToReceiveAudio: true,
  offerToReceiveVideo: true
};

// ICE server configuration
const peerConnectionConfig = {
  iceServers: [
    {
      urls: 'turn:coturn.teamusages.qa.protectline.fr:3478',
      username: 'coturnqa',
      credential: 'coturnqa'
    },
    {
      urls: 'stun:stun.l.google.com:19302' // Fallback STUN server
    }
  ]
};

@Injectable({
  providedIn: 'root'
})
export class VideoService {
  public type: string = "";
  public path: Path;
  public peerConnection: RTCPeerConnection;
  public remoteVideo: ElementRef;
  public socket$: WebSocketSubject<Message>;
  public messagesSubject$ = new Subject<any>();
  public messages$ = this.messagesSubject$.asObservable();
  public messages: string[] = [];
  public myUsername: string;
  public vehicule: Vehicule;
  private messageSubscription: Subscription;

  public pingPong: boolean = false;
  public stream: boolean = false;
  public playback: boolean = false;
  public socketLoading: boolean = false;
  public counterLoading: boolean = false;
  public connexion: boolean = false;
  public loadingStream: boolean = false;
  public camChuaffeur: boolean = false;
  public camFrontFacing: boolean = false;
  public camRearFacing: boolean = false;
  public counter: number = 0;
  private countdownInterval: any;

  constructor(
      public dataManagementService: DataManagementService,
      private toastController: ToastController,
  ) {
    this.vehicule = this.dataManagementService.selectedVehicule;
    this.createPeerConnection();
  }

  /**
   * Display status toast message
   */
  async displayStatus(message: string, type: 'success' | 'info' | 'warning' | 'error' = 'info') {
    let color: string;
    switch (type) {
      case 'success':
        color = 'success';
        break;
      case 'warning':
        color = 'warning';
        break;
      case 'error':
        color = 'danger';
        break;
      default:
        color = 'primary';
        break;
    }
    const toast = await this.toastController.create({
      message: message,
      duration: 4000,
      position: 'bottom',
      color: color,
      buttons: [{
        text: 'X',
        role: 'cancel'
      }]
    });
    await toast.present();
  }


  /**
   * Create a new WebSocket connection
   */
  private getNewWebSocket(): WebSocketSubject<any> {
    return webSocket({
      url: wsocket,
      openObserver: {
        next: () => {
          console.log('WebSocket connection established successfully');
          this.connexion = true;
          this.displayStatus('Connection established', 'success');
        }
      },
      closeObserver: {
        next: (event) => {
          console.log('WebSocket connection closed', event.reason);
          this.connexion = false;
          this.displayStatus('Connection closed: ' + (event.reason || 'Unknown reason'), 'warning');
        }
      }
    });
  }

  /**
   * Connect to WebSocket server
   */
  public connect() {
    this.socketLoading = true;

    try {
      // Create new WebSocket connection
      this.socket$ = this.getNewWebSocket();

      // Subscribe to incoming messages
      this.socket$.subscribe(
          (msg: any) => {
            // Handle incoming messages
            this.messagesSubject$.next(msg);
            this.socketLoading = false;
          },
          (error: any) => {
            // Handle connection errors
            console.error('WebSocket error:', error);
            this.connexion = false;
            this.socketLoading = false;
            this.displayStatus('Connection error. Please try again.', 'error');
          },
          () => {
            // Handle connection close
            console.log('WebSocket connection closed');
            this.connexion = false;
            this.socketLoading = false;
            this.displayStatus('Connection closed', 'warning');
          }
      );

      return this.socket$;
    } catch (error) {
      console.error('Error creating WebSocket connection:', error);
      this.socketLoading = false;
      this.displayStatus('Failed to establish connection', 'error');
      return null;
    }
  }

  /**
   * Send registration message to server
   */
  sendToRegisterServer(msg: any) {
    this.sendMessage({
      event: "register-client",
      data: msg
    });
  }

  /**
   * Send message through WebSocket
   */
  public sendMessage(msg: any): void {
    if (!this.socket$ ) {
      console.error('Cannot send message: WebSocket not connected');
      this.displayStatus('Connection error: Not connected to server', 'error');
      return;
    }

    console.log('Sending message:', JSON.stringify(msg));
    this.socket$.next(msg);
  }

  /**
   * Handle hangup message from peer
   */
  public handleHangupMessage(): void {
    console.log('Received hangup message from peer');
    this.closeVideoCall();
    this.displayStatus('Call ended by remote peer', 'info');
  }

  /**
   * Handle ICE candidate message
   */
  async handleICECandidateMessage(msg: any): Promise<void> {
    try {
      const candidate = new RTCIceCandidate(msg.data.candidate);
      console.log('Adding received ICE candidate:', JSON.stringify(candidate));
      await this.peerConnection.addIceCandidate(candidate);
    } catch (error) {
      console.error('Error adding ICE candidate:', error);
      this.displayStatus('Connection error: Failed to process network information', 'error');
    }
  }

  /**
   * Handle video offer message
   */
  async handleVideoOfferMsg(msg: any) {
    try {
      const desc = new RTCSessionDescription(msg.data.sdp);
      console.log("Setting remote SDP for offer");

      await this.peerConnection.setRemoteDescription(desc);
      console.log("Creating answer");

      const answer = await this.peerConnection.createAnswer();
      await this.peerConnection.setLocalDescription(answer);

      console.log("Sending answer to remote peer");
      this.sendMessage({
        event: "message",
        data: {
          source: this.myUsername,
          target: this.vehicule.device.streamId,
          type: 'video-answer',
          sdp: this.peerConnection.localDescription
        }
      });
    } catch (error) {
      console.error('Error handling video offer:', error);
      this.displayStatus('Failed to process video offer', 'error');
    }
  }

  /**
   * Handle video answer message
   */
  async handleVideoAnswerMsg(msg: any) {
    console.log("Call recipient has accepted our call");

    try {
      if (msg.data && msg.data.sdp) {
        const remotedesc = new RTCSessionDescription(msg.data.sdp);
        await this.peerConnection.setRemoteDescription(remotedesc);
        this.displayStatus('Connection established', 'success');
      } else {
        console.error('Invalid message format for video answer:', msg);
        this.displayStatus('Invalid response from server', 'error');
      }
    } catch (error) {
      console.error('Error setting remote description:', error);
      this.displayStatus('Failed to process video answer', 'error');
    }
  }

  /**
   * Handle new ICE candidate message
   */
  async handleNewICECandidateMsg(msg: any): Promise<void> {
    try {
      const candidate = new RTCIceCandidate(msg.data.candidate);
      console.log('Adding received NEW_ICE candidate');
      await this.peerConnection.addIceCandidate(candidate);
    } catch (error) {
      console.error('Error adding NEW ICE candidate:', error);
      this.displayStatus('Connection error: Failed to process network route', 'error');
    }
  }

  /**
   * Add handlers for incoming messages
   */
  private addIncomingMessageHandler(name: string): void {
    // Unsubscribe from previous subscription if exists
    if (this.messageSubscription) {
      this.messageSubscription.unsubscribe();
    }

    console.log("Subscribing to incoming messages");
    this.messageSubscription = this.messages$.pipe(
        tap(msg => {
          if (msg.data != null) {
            const msgType = msg.data.type;
            console.log(`Received message of type: ${msgType}`);

            switch (msgType) {
              case 'hangup':
                this.handleHangupMessage();
                break;
              case 'ice-candidate':
                this.handleICECandidateMessage(msg);
                break;
              case "video-offer":
                this.handleVideoOfferMsg(msg);
                break;
              case "video-answer":
                this.handleVideoAnswerMsg(msg);
                break;
              case "video-response":
                console.log("Received video response");
                this.displayStatus('Video request acknowledged', 'info');
                break;
              case "new-ice-candidate":
                this.handleNewICECandidateMsg(msg);
                break;
              default:
                console.log("Unhandled message type");
                break;
            }
          } else if (msg.event == "ping") {
            this.pingPong = !this.pingPong;
            this.sendPongToServer(name);
          }
        })
    ).subscribe(
        (msg: any) => {
          this.messages.push(msg);
        },
        (error) => {
          console.error('Error in message subscription:', error);
          this.displayStatus('Connection error: Failed to process messages', 'error');
        }
    );
  }

  /**
   * Set username and register with server
   */
  setUsername(name: string) {
    this.myUsername = name;
    this.sendToRegisterServer({
      id: name,
      date: Date.now(),
      peerType: "mobile",
    });
  }

  /**
   * Disconnect WebSocket
   */
  disconnectSocket(vehicule: Vehicule) {
    // First hangup if streaming
    if (this.stream || this.playback) {
      this.hangUpCall(vehicule);
    }

    // Reset video element
    if (this.remoteVideo && this.remoteVideo.nativeElement) {
      this.remoteVideo.nativeElement.srcObject = null;
      this.remoteVideo.nativeElement.removeAttribute('src');
      this.remoteVideo.nativeElement.load();
    }

    // Close WebSocket connection
    if (this.socket$) {
      console.log('Closing WebSocket connection');
      this.socket$.complete();
      this.socket$ = null;
      this.displayStatus('Disconnected from server', 'info');
    }

    // Unsubscribe from message subscription
    if (this.messageSubscription) {
      this.messageSubscription.unsubscribe();
      this.messageSubscription = null;
    }

    // Reset connection state
    this.connexion = false;
  }

  /**
   * Hang up call
   */
  public hangUpCall(vehicule: Vehicule) {
    try {
      console.log('Hanging up call with:', vehicule);

      // Send hangup message to peer
      this.sendMessage({
        event: "message",
        data: {
          source: this.myUsername,
          target: this.vehicule.device.streamId,
          type: "hang-up"
        }
      });

      // Reset stream state
      this.stream = false;
      this.playback = false;

      // Close the WebRTC connection
      this.closeVideoCall();
      this.displayStatus('Call ended', 'info');

      // Start countdown for reconnection
      this.countBackToConnect();
    } catch (error) {
      console.error('Error hanging up call:', error);
      this.displayStatus('Error ending call', 'error');
    }
  }

  /**
   * Start streaming or playback based on type
   */
  streamOrPlayback(param: string, paramPlayback: string) {
    if (this.type === "stream") {
      this.Steam(param);
    } else {
      // Format date for playback
      const startDate: Date = new Date(this.path.displayBeginPathTime);
      const formattedMonth = (startDate.getMonth() + 1).toString().padStart(2, '0');
      const formattedDay = startDate.getDate().toString().padStart(2, '0');
      const formattedHour = (startDate.getHours() - 1).toString().padStart(2, '0');
      const formattedMinutes = startDate.getMinutes().toString().padStart(2, '0');

      const date = `${startDate.getFullYear()}${formattedMonth}${formattedDay}-${formattedHour}${formattedMinutes}`;
      this.Playback(date, paramPlayback);
    }
  }

  /**
   * Reset service to initial state
   */
  public resetService(): void {
    // Cancel any active countdown
    if (this.countdownInterval) {
      clearInterval(this.countdownInterval);
      this.countdownInterval = null;
    }

    // Reset all properties
    this.type = "";
    this.path = null;
    this.remoteVideo = null;

    if (this.socket$) {
      this.socket$.complete();
      this.socket$ = null;
    }

    if (this.messageSubscription) {
      this.messageSubscription.unsubscribe();
      this.messageSubscription = null;
    }

    this.messagesSubject$ = new Subject<any>();
    this.messages$ = this.messagesSubject$.asObservable();
    this.messages = [];
    this.myUsername = "";
    this.pingPong = false;
    this.stream = false;
    this.playback = false;
    this.socketLoading = false;
    this.counterLoading = false;
    this.connexion = false;
    this.loadingStream = false;
    this.camChuaffeur = false;
    this.camFrontFacing = false;
    this.camRearFacing = false;
    this.counter = 0;

    // Create new peer connection
    if (this.peerConnection) {
      this.peerConnection.close();
    }
    this.createPeerConnection();

    // Set current vehicle
    this.vehicule = this.dataManagementService.selectedVehicule;
  }

  /**
   * Start streaming from selected camera
   */
  async Steam(param: string): Promise<void> {
    // Set active camera flags
    switch (param) {
      case "S5":
        this.camChuaffeur = true;
        this.camFrontFacing = false;
        this.camRearFacing = false;
        this.displayStatus('Switching to driver camera', 'info');
        break;
      case "S4":
        this.camChuaffeur = false;
        this.camFrontFacing = true;
        this.camRearFacing = false;
        this.displayStatus('Switching to front camera', 'info');
        break;
      default: // S6
        this.camChuaffeur = false;
        this.camFrontFacing = false;
        this.camRearFacing = true;
        this.displayStatus('Switching to rear camera', 'info');
        break;
    }

    this.loadingStream = true;
    console.log("Selected vehicle for streaming:", this.vehicule);

    try {
      // Send appropriate message based on stream state
      if (!this.stream) {
        // Initial video request
        this.displayStatus('Requesting video stream...', 'info');
        this.sendMessage({
          event: 'message',
          data: {
            source: this.myUsername,
            type: "video-request",
            infos: param,
            target: this.vehicule.device.streamId
          }
        });
      } else {
        // Switch camera request
        this.sendMessage({
          event: 'message',
          data: {
            source: this.myUsername,
            type: "switch-cam",
            infos: param,
            target: this.vehicule.device.streamId
          }
        });
      }

      // Create and set offer
      const offer: RTCSessionDescriptionInit = await this.peerConnection.createOffer(offerOptions);
      await this.peerConnection.setLocalDescription(offer);

      // Update stream state
      this.stream = true;
      this.loadingStream = false;
    } catch (error) {
      console.error("Could not set local description:", error);
      this.loadingStream = false;
      this.displayStatus('Failed to start stream', 'error');
    }
  }

  /**
   * Start playback of recorded video
   */
  async Playback(date: string, param: string): Promise<void> {
    // Set active camera flags
    switch (param) {
      case "CH2":
        this.camChuaffeur = true;
        this.camFrontFacing = false;
        this.camRearFacing = false;
        this.displayStatus('Loading driver camera recording', 'info');
        break;
      case "CH1":
        this.camChuaffeur = false;
        this.camFrontFacing = true;
        this.camRearFacing = false;
        this.displayStatus('Loading front camera recording', 'info');
        break;
      default: // CH3
        this.camChuaffeur = false;
        this.camFrontFacing = false;
        this.camRearFacing = true;
        this.displayStatus('Loading rear camera recording', 'info');
        break;
    }

    this.loadingStream = true;
    console.log("Selected vehicle for playback:", this.vehicule);

    try {
      // Send appropriate message based on playback state
      if (!this.playback) {
        // Initial playback request
        this.displayStatus('Requesting video playback...', 'info');
        this.sendMessage({
          event: 'message',
          data: {
            source: this.myUsername,
            type: "video-request",
            infos: `PNormal/${param}/${param}REC${date}`,
            target: this.vehicule.device.streamId
          }
        });
      } else {
        // Switch camera for playback
        this.sendMessage({
          event: 'message',
          data: {
            source: this.myUsername,
            type: "switch-cam",
            infos: `PNormal/${param}/${param}REC${date}`,
            target: this.vehicule.device.streamId
          }
        });
      }

      // Create and set offer
      const offer: RTCSessionDescriptionInit = await this.peerConnection.createOffer(offerOptions);
      await this.peerConnection.setLocalDescription(offer);

      // Update playback state
      this.playback = true;
      this.loadingStream = false;
    } catch (error) {
      console.error("Could not set local description for playback:", error);
      this.loadingStream = false;
      this.displayStatus('Failed to start playback', 'error');
    }
  }

  /**
   * Handle click on connect button
   */
  onConnectClick(vehicule: Vehicule): void {
    if (!this.connexion) {
      // Create new connection
      this.displayStatus('Connecting to vehicle...', 'info');
      const connection = this.connect();

      if (connection) {
        console.log("Connecting to vehicle:", vehicule);

        // Register with server
        this.setUsername(vehicule.idDevice.toString());
        // Add message handlers
        this.addIncomingMessageHandler(vehicule.idDevice.toString());

        // Store vehicle reference
        this.vehicule = vehicule;
      }
    } else {
      this.displayStatus('Already connected', 'info');
    }
  }

  /**
   * Send pong response to server
   */
  sendPongToServer(name: string) {
    this.pingPong = !this.pingPong;
    this.sendMessage({
      event: "pong",
      data: { name: name }
    });
  }

  /**
   * Handle negotiation needed event
   */
  async handleNegotiationNeededEvent() {
    console.log('Negotiation needed');

    try {
      // Check if connection is in stable state
      if (this.peerConnection.signalingState !== 'stable') {
        console.log('Connection not stable, postponing negotiation');
        return;
      }

      console.log('Creating offer');
      const offer = await this.peerConnection.createOffer(offerOptions);

      console.log('Setting local description');
      await this.peerConnection.setLocalDescription(offer);

      console.log('Sending offer to remote peer');
      this.sendMessage({
        event: "message",
        data: {
          source: this.myUsername,
          target: this.vehicule.device.streamId,
          type: 'video-offer',
          sdp: this.peerConnection.localDescription
        }
      });
    } catch (error) {
      console.error('Error during negotiation:', error);
      this.displayStatus('Connection negotiation failed', 'error');
    }
  }

  /**
   * Handle track event (remote stream received)
   */
  private handleTrackEvent = (event: RTCTrackEvent) => {
    console.log("Track event received");

    if (this.remoteVideo && this.remoteVideo.nativeElement) {
      this.remoteVideo.nativeElement.autoplay = true;
      this.remoteVideo.nativeElement.playsInline = true;

      try {
        this.remoteVideo.nativeElement.srcObject = event.streams[0];
        this.stream = true;
        this.displayStatus('Video stream connected', 'success');
      } catch (error) {
        console.error("Error assigning stream to video element:", error);
        this.displayStatus('Failed to display video stream', 'error');
      }
    } else {
      console.error("Remote video element not available");
      this.displayStatus('Video element not available', 'error');
    }
  }

  /**
   * Handle data channel event
   */
  private handleDataChannel = () => {
    console.log("Data channel event received");
    // Implement data channel handling if needed
  }

  /**
   * Handle signaling state change event
   */
  private handleSignalingStateChangeEvent = () => {
    console.log("WebRTC signaling state changed to:", this.peerConnection.signalingState);

    if (this.peerConnection.signalingState === 'closed') {
      console.log("Connection closed");
      this.displayStatus('Connection closed', 'info');
    }
  }

  /**
   * Handle ICE gathering state change event
   */
  private handleICEGatheringStateChangeEvent = () => {
    console.log("ICE gathering state changed to:", this.peerConnection.iceGatheringState);
  }

  /**
   * Handle ICE connection state change event
   */
  private handleICEConnectionStateChangeEvent = () => {
    console.log("ICE connection state changed to:", this.peerConnection.iceConnectionState);

    switch (this.peerConnection.iceConnectionState) {
      case 'closed':
        console.log("ICE connection closed");
        this.displayStatus('Connection closed', 'info');
        break;
      case 'failed':
        console.log("ICE connection failed");
        this.displayStatus('Connection failed', 'error');
        break;
      case 'disconnected':
        console.log("ICE connection disconnected");
        this.closeVideoCall();
        this.displayStatus('Connection disconnected', 'warning');
        break;
      case 'connected':
        this.displayStatus('Network connection established', 'success');
        break;
    }
  }

  /**
   * Handle ICE candidate event
   */
  private handleICECandidateEvent = (event: RTCPeerConnectionIceEvent) => {
    if (event.candidate) {
      console.log("Outgoing ICE candidate");

      this.sendMessage({
        event: "message",
        data: {
          candidate: event.candidate,
          source: this.myUsername,
          target: this.vehicule.device.streamId,
          type: 'new-ice-candidate',
        }
      });
    }
  }

  /**
   * Create peer connection with proper event handlers
   */
  public createPeerConnection(): void {
    // Create new peer connection with ICE servers
    this.peerConnection = new RTCPeerConnection(peerConnectionConfig);

    // Set event handlers
    this.peerConnection.onicecandidate = this.handleICECandidateEvent;
    this.peerConnection.oniceconnectionstatechange = this.handleICEConnectionStateChangeEvent;
    this.peerConnection.onicegatheringstatechange = this.handleICEGatheringStateChangeEvent;
    this.peerConnection.onsignalingstatechange = this.handleSignalingStateChangeEvent;
    this.peerConnection.ondatachannel = this.handleDataChannel;
    this.peerConnection.ontrack = this.handleTrackEvent;
    this.peerConnection.onnegotiationneeded = this.handleNegotiationNeededEvent.bind(this);

    console.log("Peer connection created with config:", peerConnectionConfig);
  }

  /**
   * Close video call and clean up resources
   */
  private closeVideoCall(): void {
    this.loadingStream = true;

    try {
      // Clear all event handlers
      if (this.peerConnection) {
        this.peerConnection.ontrack = null;
        this.peerConnection.onicecandidate = null;
        this.peerConnection.oniceconnectionstatechange = null;
        this.peerConnection.onsignalingstatechange = null;
        this.peerConnection.onnegotiationneeded = null;
        this.peerConnection.ondatachannel = null;

        // Stop all transceivers
        this.peerConnection.getTransceivers().forEach(transceiver => {
          transceiver.stop();
        });

        // Close the connection
        this.peerConnection.close();
        this.peerConnection = null;
      }
    } catch (error) {
      console.error("Error closing video call:", error);
      this.displayStatus('Error closing video connection', 'error');
    } finally {
      // Reset state
      this.stream = false;
      this.playback = false;
      this.loadingStream = false;
      this.camChuaffeur = false;
      this.camRearFacing = false;
      this.camFrontFacing = false;

      // Create new peer connection
      this.createPeerConnection();
    }
  }

  /**
   * Start countdown for reconnection
   */
  countBackToConnect(): void {
    // Clear any existing interval
    if (this.countdownInterval) {
      clearInterval(this.countdownInterval);
    }

    // Set initial state
    this.counterLoading = true;
    this.counter = 30;
    this.displayStatus('Reconnect available in 30 seconds', 'info');

    // Start countdown interval
    this.countdownInterval = setInterval(() => {
      this.counter--;

      if (this.counter <= 0) {
        clearInterval(this.countdownInterval);
        this.countdownInterval = null;
        this.counterLoading = false;
        this.displayStatus('Ready to reconnect', 'success');
      }

      console.log("Reconnect countdown:", this.counter);
    }, 1000);
  }
}
